import { Controller } from "react-hook-form";
import {
  destinationTeamCreateSchema,
  type DestinationTeamCreateInput,
} from "@ckgo2/shared/schemas";
import { TEAM_SECTION_TYPES } from "@ckgo2/shared/types";
import type { DestinationTeamSection, Destination } from "@ckgo2/shared/types";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import MediaPicker from "@/components/common/MediaPicker";
import EditPageHeader from "@/components/common/EditPageHeader";
import { useEditPage } from "@/hooks/useEditPage";
import { Plus, X } from "lucide-react";

const SECTION_LABELS: Record<string, string> = {
  guarantor: "Guarantor",
  sales_team: "Sales Team",
  guide: "Guide",
  local_team: "Local Team",
};

export default function DestinationTeamEditPage() {
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    DestinationTeamCreateInput,
    DestinationTeamSection
  >({
    resourceKey: "destination-team",
    listKey: "destination-teams",
    apiPath: "/api/admin/destination-teams",
    schema: destinationTeamCreateSchema,
    defaultValues: {
      destinationId: "",
      sectionType: "guarantor",
      name: "",
      subtitle: "",
      bio: "",
      email: "",
      phone: "",
      photo: "",
      photoMediaId: null,
      photos: [],
      photoIds: [],
      members: [],
      sortOrder: 0,
      isActive: true,
    },
    navigateTo: "/destination-teams",
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Fetch destinations for select
  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });
  const destinations = destinationsData?.data || [];

  const members = watch("members") || [];

  const addMember = () => {
    setValue("members", [...members, { name: "", role: "", photoId: null }], { shouldDirty: true });
  };

  const removeMember = (index: number) => {
    setValue(
      "members",
      members.filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  const updateMember = (index: number, patch: Partial<(typeof members)[number]>) => {
    const updated = [...members];
    updated[index] = { ...updated[index], ...patch };
    setValue("members", updated, { shouldDirty: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/destination-teams"
        isNew={isNew}
        newTitle="New team section"
        editTitle="Edit team section"
        entityName={itemData?.name}
        formId="team-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
      />

      {Object.keys(errors).length > 0 && (
        <div className="border-error-200 bg-error-50 text-error-700 rounded-lg border px-4 py-3 text-sm">
          Some fields need attention before saving — check the highlighted inputs below.
        </div>
      )}

      <form id="team-form" onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Basic information
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="label">
                  Name *
                </label>
                <input
                  {...register("name")}
                  type="text"
                  id="name"
                  className={`input ${errors.name ? "input-error" : ""}`}
                  placeholder="e.g. John Smith"
                />
                {errors.name && (
                  <p className="text-error-600 mt-1 text-sm">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="sectionType" className="label">
                  Section type *
                </label>
                <select {...register("sectionType")} id="sectionType" className="input">
                  {TEAM_SECTION_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {SECTION_LABELS[t] || t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="destinationId" className="label">
                  Destination *
                </label>
                <select
                  {...register("destinationId")}
                  id="destinationId"
                  className={`input ${errors.destinationId ? "input-error" : ""}`}
                >
                  <option value="">Select destination</option>
                  {destinations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {errors.destinationId && (
                  <p className="text-error-600 mt-1 text-sm">{errors.destinationId.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="subtitle" className="label">
                  Subtitle / Role
                </label>
                <input
                  {...register("subtitle")}
                  type="text"
                  id="subtitle"
                  className="input"
                  placeholder="e.g. Destination Guarantor for Vietnam"
                />
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <input
                  {...register("email")}
                  type="email"
                  id="email"
                  className={`input ${errors.email ? "input-error" : ""}`}
                  placeholder="e.g. mai@banh-mi-escape.com"
                />
                {errors.email && (
                  <p className="text-error-600 mt-1 text-sm">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="label">
                  Phone
                </label>
                <input
                  {...register("phone")}
                  type="text"
                  id="phone"
                  className="input"
                  placeholder="e.g. +84 966 680 323"
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Biography / Description
            </h2>
            <textarea
              {...register("bio")}
              id="bio"
              rows={5}
              className="input"
              placeholder="Tell us about this person or team..."
            />
          </div>

          {/* Team Members (for sales_team and local_team) */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team Members</h2>
              <button type="button" onClick={addMember} className="btn-ghost btn-sm">
                <Plus className="h-4 w-4" />
                Add member
              </button>
            </div>
            <p className="mb-4 text-sm text-gray-500">
              For team sections (Sales Team, Local Team), list individual team members.
            </p>
            {members.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">No members added yet</p>
            ) : (
              <div className="space-y-4">
                {members.map((member, index) => (
                  <div
                    key={index}
                    className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateMember(index, { name: e.target.value })}
                        className={`input flex-1 ${errors.members?.[index]?.name ? "input-error" : ""}`}
                        placeholder="Name"
                      />
                      <input
                        type="text"
                        value={member.role || ""}
                        onChange={(e) => updateMember(index, { role: e.target.value })}
                        className="input w-48"
                        placeholder="Role (optional)"
                      />
                      <button
                        type="button"
                        onClick={() => removeMember(index)}
                        className="btn-ghost btn-sm text-error-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {errors.members?.[index]?.name && (
                      <p className="text-error-600 text-sm">
                        {errors.members[index]?.name?.message}
                      </p>
                    )}
                    <div>
                      <label className="label">Photo</label>
                      <MediaPicker
                        mode="single"
                        value={member.photoId ?? null}
                        onChange={(id) => updateMember(index, { photoId: id })}
                        folder="teams"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Photo Gallery */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Photo Gallery
            </h2>
            <Controller
              name="photoIds"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="multiple"
                  value={field.value || []}
                  onChange={field.onChange}
                  folder="teams"
                  label="Upload team photos"
                />
              )}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Main Photo</h2>
            <Controller
              name="photoMediaId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="single"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  folder="teams"
                />
              )}
            />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="sortOrder" className="label">
                  Sort order
                </label>
                <input
                  {...register("sortOrder", { valueAsNumber: true })}
                  type="number"
                  id="sortOrder"
                  className="input"
                  min={0}
                />
                <p className="mt-1 text-xs text-gray-500">Lower = higher priority</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  {...register("isActive")}
                  type="checkbox"
                  id="isActive"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Active (visible on website)
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
